import React, {useMemo, useState} from 'react'
import type {VendorLogisticsChangeRequest} from '../core/_requests'
import {hasChangedVendorLogisticsValue} from '../core/vendorLogisticsChangeRequests'

type Props = {
  open: boolean
  request: VendorLogisticsChangeRequest | null
  canReview: boolean
  submitting?: boolean
  onClose: () => void
  onApprove?: (requestId: number) => Promise<void> | void
  onReject?: (requestId: number, reason: string) => Promise<void> | void
}

type ValueRow = {
  label: string
  oldValue: number | null | undefined
  requestedValue: number | null | undefined
}

function formatMoney(value: number | null | undefined, currency?: string | null): string {
  const amount = Number(value ?? 0)
  const code = String(currency || 'USD').trim().toUpperCase() || 'USD'
  return `${code} ${amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
}

const VendorLogisticsChangeRequestModal: React.FC<Props> = ({
  open,
  request,
  canReview,
  submitting = false,
  onClose,
  onApprove,
  onReject,
}) => {
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectInput, setShowRejectInput] = useState(false)
  const [rejectError, setRejectError] = useState('')

  const valueRows = useMemo<ValueRow[]>(
    () => [
      {label: 'Freight / Trucking', oldValue: request?.oldFreightCost, requestedValue: request?.requestedFreightCost},
      {label: 'Packaging & Handling', oldValue: request?.oldPackagingCost, requestedValue: request?.requestedPackagingCost},
      {label: 'Truck Cost', oldValue: request?.oldTruckCost, requestedValue: request?.requestedTruckCost},
      {label: 'Barge / Workboat', oldValue: request?.oldBargeCost, requestedValue: request?.requestedBargeCost},
      {label: 'Other Cost', oldValue: request?.oldOtherCost, requestedValue: request?.requestedOtherCost},
      {label: 'VAT/GST on Logistics', oldValue: request?.oldVatOnLogistics, requestedValue: request?.requestedVatOnLogistics},
      {label: 'Total Discount', oldValue: request?.oldTotalDiscount, requestedValue: request?.requestedTotalDiscount},
      {label: 'Quotation Total', oldValue: request?.oldQuotationTotal, requestedValue: request?.requestedQuotationTotal},
    ],
    [request]
  )

  if (!open || !request) return null

  const handleReject = async () => {
    const reason = rejectReason.trim()
    if (!reason) {
      setRejectError('Rejection reason is required.')
      return
    }
    setRejectError('')
    await onReject?.(request.id, reason)
  }

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center'
      style={{backgroundColor: 'rgba(0,0,0,0.45)', position: 'fixed', inset: 0, zIndex: 1050}}
    >
      <div className='modal-dialog modal-xl modal-dialog-centered'>
        <div className='modal-content bg-white'>
          <div className='modal-header'>
            <div>
              <h5 className='modal-title mb-1'>Review Logistics Update</h5>
              <div className='text-muted small'>
                {request.poNumber || request.purchaseOrderId || '-'} · {request.vendorName || request.vendorId || '-'}
              </div>
            </div>
            <button type='button' className='btn-close' disabled={submitting} onClick={onClose} />
          </div>
          <div className='modal-body'>
            <div className='row g-3 mb-4'>
              <div className='col-md-4'><strong>RFQ:</strong> {request.rfqCode || request.rfqId || '-'}</div>
              <div className='col-md-4'><strong>Requisition:</strong> {request.requisitionCode || request.requisitionId || '-'}</div>
              <div className='col-md-4'><strong>Requested By:</strong> {request.requestedByUsername || '-'}</div>
              <div className='col-md-4'><strong>Vendor:</strong> {request.vendorName || request.vendorId || '-'}</div>
              <div className='col-md-4'><strong>PO:</strong> {request.poNumber || request.purchaseOrderId || '-'}</div>
              <div className='col-md-4'><strong>Request Date:</strong> {request.requestedAt ? new Date(request.requestedAt).toLocaleString() : '-'}</div>
            </div>
            <div className='table-responsive'>
              <table className='table table-bordered align-middle'>
                <thead className='table-light'>
                  <tr>
                    <th>Field</th>
                    <th>Current</th>
                    <th>Requested</th>
                  </tr>
                </thead>
                <tbody>
                  {valueRows.map((row) => {
                    const changed = hasChangedVendorLogisticsValue(row.oldValue, row.requestedValue)
                    return (
                      <tr key={row.label}>
                        <td className='fw-semibold'>{row.label}</td>
                        <td>{formatMoney(row.oldValue, request.currency)}</td>
                        <td className={changed ? 'bg-light-warning fw-bold text-dark' : ''}>
                          {formatMoney(row.requestedValue, request.currency)}
                        </td>
                      </tr>
                    )
                  })}
                  <tr>
                    <td className='fw-semibold'>Difference</td>
                    <td>-</td>
                    <td className='bg-light-info fw-bold text-dark'>
                      {formatMoney(request.totalDifference, request.currency)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className='mt-4'>
              <label className='form-label fw-semibold'>Vendor Remarks</label>
              <div className='form-control bg-light' style={{minHeight: 88}}>
                {request.vendorRemarks?.trim() || 'No remarks provided.'}
              </div>
            </div>
            {showRejectInput && canReview ? (
              <div className='mt-4'>
                <label className='form-label fw-semibold'>Rejection Reason *</label>
                <textarea
                  className='form-control'
                  rows={4}
                  value={rejectReason}
                  onChange={(event) => {
                    setRejectReason(event.target.value)
                    if (rejectError) setRejectError('')
                  }}
                />
                {rejectError ? <div className='text-danger small mt-2'>{rejectError}</div> : null}
              </div>
            ) : null}
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-light' disabled={submitting} onClick={onClose}>
              Close
            </button>
            {canReview ? (
              <>
                {!showRejectInput ? (
                  <button
                    type='button'
                    className='btn btn-danger'
                    disabled={submitting}
                    onClick={() => setShowRejectInput(true)}
                  >
                    Reject
                  </button>
                ) : (
                  <button
                    type='button'
                    className='btn btn-danger'
                    disabled={submitting}
                    onClick={() => void handleReject()}
                  >
                    Confirm Reject
                  </button>
                )}
                <button
                  type='button'
                  className='btn btn-success'
                  disabled={submitting}
                  onClick={() => void onApprove?.(request.id)}
                >
                  Approve
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default VendorLogisticsChangeRequestModal
